import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {ProcessedChartDataDTO} from '#app/api';
import {RatingGroup} from '#app/components/funnel/shared/tables/types';

export interface UserTableClientDTO {
    id: number;
    name: string;
    sumServices: number;
    sumActives: number;
    sumServicesPercent?: number;
    sumActivesPercent?: number;
}

export interface ClientActiveDTO {
    user_id: string | number;
    valuation?: number;
    type_id?: number;
    name_text?: string;
    account_name?: string;

    [key: string]: any;
}

export type RevenueByPeriodDTO = {
    '1m': number;
    '3m': number;
    '1y': number;
    total: number;
};

/** Кэш оценок рейтинга по режиму «мои / фин. группа» (для двойной загрузки и мгновенного переключения). */
export interface RatingValuationScopeSnapshot {
    groups: RatingGroup[];
    clientRevenueByPeriod: Record<string, RevenueByPeriodDTO>;
    clientAssetsMap: Record<string, number>;
    scopeKey: string;
    bootstrapKey: string;
}

export interface FunnelDataState {
    ratingGroups: RatingGroup[];
    ratingGroupsOriginal: RatingGroup[];
    ratingGroupsLoading: boolean;
    ratingGroupsError: string | null;
    ratingGroupsLastUpdated: number | null;
    ratingClientRevenueByPeriod: Record<string, RevenueByPeriodDTO>;
    ratingClientAssetsMap: Record<string, number>;

    userTableClients: UserTableClientDTO[];
    userTableClientsOriginal: UserTableClientDTO[];
    userTableLoading: boolean;
    userTableError: string | null;
    userTableLastUpdated: number | null;

    chartData: ProcessedChartDataDTO | null;
    chartDataOriginal: ProcessedChartDataDTO | null;
    chartDataLoading: boolean;
    chartDataError: string | null;
    chartDataLastUpdated: number | null;

    activesData: ClientActiveDTO[];
    activesDataOriginal: ClientActiveDTO[];
    activesDataLoading: boolean;
    activesDataError: string | null;
    activesDataLastUpdated: number | null;

    currentManagerId: number | null;
    currentAtonValuation: number | null;
    currentSelectedRating: number | null;
    currentSelectedClientIds: number[];
    currentFinanceGroupIncluded: boolean;
    clientsCount: number;
    chartScopeSources: {
        my: {
            clients: ClientActiveDTO[];
            clientIds: number[];
            accountNumbers: string[];
        };
        financeGroup: {
            clients: ClientActiveDTO[];
            clientIds: number[];
            accountNumbers: string[];
        };
    };
    /**
     * CRM client `id` from merged funnel clients (`client/index` + service.client) — same cohort as `sum_valuation`.
     * Used for ATON `users_today_valuation` scope in «Активы» so totals align with statistics valuation.
     */
    mergedFunnelClientPrimaryKeys: number[];
    valuationByScope: {
        my: any[];
        financeGroup: any[];
    };

    ratingValuationScopeSnapshots: {
        my: RatingValuationScopeSnapshot | null;
        financeGroup: RatingValuationScopeSnapshot | null;
    };
    /** Оба scope загружены (вторая фоновая попытка завершена); до этого переключатель режима блокируется. */
    ratingValuationScopesBootstrapReady: boolean;
}

const initialState: FunnelDataState = {
    ratingGroups: [],
    ratingGroupsOriginal: [],
    ratingGroupsLoading: false,
    ratingGroupsError: null,
    ratingGroupsLastUpdated: null,
    ratingClientRevenueByPeriod: {},
    ratingClientAssetsMap: {},

    userTableClients: [],
    userTableClientsOriginal: [],
    userTableLoading: false,
    userTableError: null,
    userTableLastUpdated: null,

    chartData: null,
    chartDataOriginal: null,
    chartDataLoading: false,
    chartDataError: null,
    chartDataLastUpdated: null,

    activesData: [],
    activesDataOriginal: [],
    activesDataLoading: false,
    activesDataError: null,
    activesDataLastUpdated: null,

    currentManagerId: null,
    currentAtonValuation: null,
    currentSelectedRating: null,
    currentSelectedClientIds: [],
    currentFinanceGroupIncluded: true,
    clientsCount: 0,
    chartScopeSources: {
        my: {
            clients: [],
            clientIds: [],
            accountNumbers: [],
        },
        financeGroup: {
            clients: [],
            clientIds: [],
            accountNumbers: [],
        },
    },
    mergedFunnelClientPrimaryKeys: [],
    valuationByScope: {
        my: [],
        financeGroup: [],
    },

    ratingValuationScopeSnapshots: {
        my: null,
        financeGroup: null,
    },
    ratingValuationScopesBootstrapReady: false,
};

/**
 *
 * @param firstValue
 * @param secondValue
 */
const deepEqual = (firstValue: any, secondValue: any): boolean => {
    if (firstValue === secondValue) return true;
    if (firstValue == null || secondValue == null) return false;
    if (typeof firstValue !== typeof secondValue) return false;

    if (Array.isArray(firstValue) && Array.isArray(secondValue)) {
        if (firstValue.length !== secondValue.length) return false;
        return firstValue.every((item, index) => deepEqual(item, secondValue[index]));
    }

    if (typeof firstValue === 'object' && typeof secondValue === 'object') {
        const firstObj = firstValue as Record<string, any>;
        const secondObj = secondValue as Record<string, any>;
        const firstKeys = Object.keys(firstObj);
        const secondKeys = Object.keys(secondObj);
        if (firstKeys.length !== secondKeys.length) return false;
        return firstKeys.every((key) => deepEqual(firstObj[key], secondObj[key]));
    }

    return false;
};

/**
 *
 * @param firstClients
 * @param secondClients
 */
const compareUserTableClients = (firstClients: UserTableClientDTO[], secondClients: UserTableClientDTO[]): boolean => {
    if (firstClients.length !== secondClients.length) return false;

    return firstClients.every((clientA, index) => {
        const clientB = secondClients[index];
        return (
            clientA.id === clientB.id &&
            clientA.name === clientB.name &&
            clientA.sumServices === clientB.sumServices &&
            clientA.sumActives === clientB.sumActives &&
            (clientA.sumServicesPercent || 0) === (clientB.sumServicesPercent || 0) &&
            (clientA.sumActivesPercent || 0) === (clientB.sumActivesPercent || 0)
        );
    });
};

// Функция compareRatingGroups удалена, так как она не использовалась

export const funnelDataSlice = createSlice({
    name: 'funnelData',
    initialState,
    reducers: {
        resetFunnelDataState: (state) => {
            Object.assign(state, initialState);
        },
        setRatingGroups: (state, action: PayloadAction<RatingGroup[]>) => {
            state.ratingGroups = action.payload;
            state.ratingGroupsLastUpdated = Date.now();
            state.ratingGroupsError = null;
        },
        setRatingGroupsOriginal: (state, action: PayloadAction<RatingGroup[]>) => {
            state.ratingGroupsOriginal = JSON.parse(JSON.stringify(action.payload));
            state.ratingGroups = JSON.parse(JSON.stringify(action.payload));
            state.ratingGroupsLastUpdated = Date.now();
        },

        restoreRatingGroupsFromOriginal: (state) => {
            if (state.ratingGroupsOriginal.length > 0) {
                state.ratingGroups = JSON.parse(
                    JSON.stringify(state.ratingGroupsOriginal)
                );
                state.ratingGroupsLastUpdated = Date.now();
                state.ratingGroupsError = null;
            }
        },
        setRatingGroupsLoading: (state, action: PayloadAction<boolean>) => {
            state.ratingGroupsLoading = action.payload;
        },
        setRatingAggregationMeta: (
            state,
            action: PayloadAction<{
                clientRevenueByPeriod: Record<string, RevenueByPeriodDTO>;
                clientAssetsMap: Record<string, number>;
            }>
        ) => {
            state.ratingClientRevenueByPeriod = action.payload.clientRevenueByPeriod;
            state.ratingClientAssetsMap = action.payload.clientAssetsMap;
            state.ratingGroupsLastUpdated = Date.now();
            state.ratingGroupsError = null;
        },
        setRatingGroupsError: (state, action: PayloadAction<string | null>) => {
            state.ratingGroupsError = action.payload;
            state.ratingGroupsLoading = false;
        },

        setUserTableClients: (state, action: PayloadAction<UserTableClientDTO[]>) => {
            if (!compareUserTableClients(state.userTableClients, action.payload)) {
                state.userTableClients = action.payload;
                state.userTableClientsOriginal = JSON.parse(JSON.stringify(action.payload));
                state.userTableLastUpdated = Date.now();
                state.userTableError = null;
            }
        },
        restoreUserTableClients: (state) => {
            if (state.userTableClientsOriginal.length > 0) {
                state.userTableClients = JSON.parse(JSON.stringify(state.userTableClientsOriginal));
                state.userTableLastUpdated = Date.now();
            }
        },
        setUserTableLoading: (state, action: PayloadAction<boolean>) => {
            state.userTableLoading = action.payload;
        },
        setUserTableError: (state, action: PayloadAction<string | null>) => {
            state.userTableError = action.payload;
            state.userTableLoading = false;
        },

        setChartData: (state, action: PayloadAction<ProcessedChartDataDTO | null>) => {
            const newData = action.payload;
            if (!newData) {
                state.chartData = null;
                state.chartDataOriginal = null;
                state.chartDataLastUpdated = Date.now();
                return;
            }
            state.chartData = newData;
            state.chartDataOriginal = JSON.parse(JSON.stringify(newData));
            state.chartDataLastUpdated = Date.now();
            state.chartDataError = null;
        },
        patchChartValuationData: (
            state,
            action: PayloadAction<{
                transfers: ProcessedChartDataDTO["filledData"][0];
                actives: ProcessedChartDataDTO["filledData"][3];
                services?: ProcessedChartDataDTO["filledData"][1];
                stars?: ProcessedChartDataDTO["filledData"][2];
            }>
        ) => {
            if (!state.chartData) return;

            const nextFilledData = [...state.chartData.filledData] as ProcessedChartDataDTO["filledData"];
            nextFilledData[0] = action.payload.transfers.map((item) => ({...item})) as ProcessedChartDataDTO["filledData"][0];
            nextFilledData[3] = action.payload.actives.map((item) => ({...item})) as ProcessedChartDataDTO["filledData"][3];
            if (action.payload.services) {
                nextFilledData[1] = action.payload.services.map((item) => ({...item})) as ProcessedChartDataDTO["filledData"][1];
            }
            if (action.payload.stars) {
                nextFilledData[2] = action.payload.stars.map((item) => ({...item})) as ProcessedChartDataDTO["filledData"][2];
            }

            state.chartData = {
                ...state.chartData,
                filledData: nextFilledData,
            };
            if (state.chartDataOriginal) {
                state.chartDataOriginal = {
                    ...state.chartDataOriginal,
                    filledData: JSON.parse(JSON.stringify(nextFilledData)),
                };
            }
            state.chartDataLastUpdated = Date.now();
            state.chartDataError = null;
        },
        restoreChartData: (state) => {
            if (state.chartDataOriginal) {
                state.chartData = JSON.parse(JSON.stringify(state.chartDataOriginal));
                state.chartDataLastUpdated = Date.now();
            }
        },
        setChartDataLoading: (state, action: PayloadAction<boolean>) => {
            state.chartDataLoading = action.payload;
        },
        setChartDataError: (state, action: PayloadAction<string | null>) => {
            state.chartDataError = action.payload;
            state.chartDataLoading = false;
        },

        setActivesData: (state, action: PayloadAction<ClientActiveDTO[]>) => {
            if (!deepEqual(state.activesData, action.payload)) {
                state.activesData = action.payload;
                state.activesDataOriginal = JSON.parse(JSON.stringify(action.payload));
                state.activesDataLastUpdated = Date.now();
                state.activesDataError = null;
            }
        },
        restoreActivesData: (state) => {
            if (state.activesDataOriginal.length > 0) {
                state.activesData = JSON.parse(JSON.stringify(state.activesDataOriginal));
                state.activesDataLastUpdated = Date.now();
            }
        },
        setActivesDataLoading: (state, action: PayloadAction<boolean>) => {
            state.activesDataLoading = action.payload;
        },
        setActivesDataError: (state, action: PayloadAction<string | null>) => {
            state.activesDataError = action.payload;
            state.activesDataLoading = false;
        },

        setFunnelParams: (
            state,
            action: PayloadAction<{
                managerId?: number | null;
                atonValuation?: number | null;
                selectedRating?: number | null;
                selectedClientIds?: number[];
                financeGroupIncluded?: boolean;
                clientsCount?: number;
            }>
        ) => {
            const {
                managerId,
                atonValuation,
                selectedRating,
                selectedClientIds,
                financeGroupIncluded,
                clientsCount
            } = action.payload;
            if (managerId !== undefined) state.currentManagerId = managerId;
            if (atonValuation !== undefined) state.currentAtonValuation = atonValuation;
            if (selectedRating !== undefined) state.currentSelectedRating = selectedRating;
            if (financeGroupIncluded !== undefined) state.currentFinanceGroupIncluded = financeGroupIncluded;
            if (clientsCount !== undefined) state.clientsCount = clientsCount;
            if (selectedClientIds !== undefined) {
                if (!deepEqual(state.currentSelectedClientIds, selectedClientIds)) {
                    state.currentSelectedClientIds = selectedClientIds;
                }
            }
        },
        setChartScopeSources: (
            state,
            action: PayloadAction<{
                my: {
                    clients: ClientActiveDTO[];
                    clientIds: number[];
                    accountNumbers: string[];
                };
                financeGroup: {
                    clients: ClientActiveDTO[];
                    clientIds: number[];
                    accountNumbers: string[];
                };
            }>
        ) => {
            state.chartScopeSources = action.payload;
        },
        setMergedFunnelClientPrimaryKeys: (state, action: PayloadAction<number[]>) => {
            state.mergedFunnelClientPrimaryKeys = Array.isArray(action.payload) ? action.payload : [];
        },
        setValuationByScope: (
            state,
            action: PayloadAction<{
                scope: "my" | "financeGroup";
                entries: any[];
            }>
        ) => {
            state.valuationByScope[action.payload.scope] = action.payload.entries;
        },

        setRatingValuationScopeSnapshot: (
            state,
            action: PayloadAction<{
                mode: 'my' | 'financeGroup';
                snapshot: RatingValuationScopeSnapshot;
            }>
        ) => {
            const {mode, snapshot} = action.payload;
            state.ratingValuationScopeSnapshots[mode] = {
                groups: JSON.parse(JSON.stringify(snapshot.groups)),
                clientRevenueByPeriod: {...snapshot.clientRevenueByPeriod},
                clientAssetsMap: {...snapshot.clientAssetsMap},
                scopeKey: snapshot.scopeKey,
                bootstrapKey: snapshot.bootstrapKey,
            };
        },

        clearRatingValuationScopeSnapshots: (state) => {
            state.ratingValuationScopeSnapshots.my = null;
            state.ratingValuationScopeSnapshots.financeGroup = null;
            state.ratingValuationScopesBootstrapReady = false;
        },

        setRatingValuationScopesBootstrapReady: (state, action: PayloadAction<boolean>) => {
            state.ratingValuationScopesBootstrapReady = action.payload;
        },

        clearFunnelData: (state) => {
            state.ratingGroups = [];
            state.ratingGroupsOriginal = [];
            state.ratingClientRevenueByPeriod = {};
            state.ratingClientAssetsMap = {};
            state.userTableClients = [];
            state.userTableClientsOriginal = [];
            state.chartData = null;
            state.chartDataOriginal = null;
            state.activesData = [];
            state.activesDataOriginal = [];
            state.ratingGroupsLastUpdated = null;
            state.userTableLastUpdated = null;
            state.chartDataLastUpdated = null;
            state.activesDataLastUpdated = null;
            state.chartScopeSources = initialState.chartScopeSources;
            state.mergedFunnelClientPrimaryKeys = initialState.mergedFunnelClientPrimaryKeys;
            state.valuationByScope = initialState.valuationByScope;
            state.ratingValuationScopeSnapshots = initialState.ratingValuationScopeSnapshots;
            state.ratingValuationScopesBootstrapReady = initialState.ratingValuationScopesBootstrapReady;
        },
    },
});

export const {
    resetFunnelDataState,
    setRatingGroups,
    setRatingGroupsOriginal,
    restoreRatingGroupsFromOriginal,
    setRatingGroupsLoading,
    setRatingAggregationMeta,
    setRatingGroupsError,
    setUserTableClients,
    restoreUserTableClients,
    setUserTableLoading,
    setUserTableError,
    setChartData,
    patchChartValuationData,
    restoreChartData,
    setChartDataLoading,
    setChartDataError,
    setActivesData,
    restoreActivesData,
    setActivesDataLoading,
    setActivesDataError,
    setFunnelParams,
    setChartScopeSources,
    setMergedFunnelClientPrimaryKeys,
    setValuationByScope,
    setRatingValuationScopeSnapshot,
    clearRatingValuationScopeSnapshots,
    setRatingValuationScopesBootstrapReady,
    clearFunnelData,
} = funnelDataSlice.actions;

// Базовый тип для селекторов
type RootState = { funnelData?: FunnelDataState };

export const selectFunnelDataState = (state: RootState): FunnelDataState =>
    state.funnelData || initialState;

export const selectRatingGroups = (state: RootState): RatingGroup[] =>
    selectFunnelDataState(state).ratingGroups;

export const selectRatingGroupsOriginal = (state: RootState): RatingGroup[] =>
    selectFunnelDataState(state).ratingGroupsOriginal;

export const selectRatingGroupsLoading = (state: RootState): boolean =>
    selectFunnelDataState(state).ratingGroupsLoading;

export const selectRatingClientRevenueByPeriod = (
    state: RootState
): Record<string, RevenueByPeriodDTO> =>
    selectFunnelDataState(state).ratingClientRevenueByPeriod;

export const selectRatingClientAssetsMap = (state: RootState): Record<string, number> =>
    selectFunnelDataState(state).ratingClientAssetsMap;

export const selectUserTableClients = (state: RootState): UserTableClientDTO[] =>
    selectFunnelDataState(state).userTableClients;

export const selectUserTableLoading = (state: RootState): boolean =>
    selectFunnelDataState(state).userTableLoading;

export const selectChartData = (state: RootState): ProcessedChartDataDTO | null =>
    selectFunnelDataState(state).chartData;

export const selectChartDataLoading = (state: RootState): boolean =>
    selectFunnelDataState(state).chartDataLoading;

export const selectActivesData = (state: RootState): ClientActiveDTO[] =>
    selectFunnelDataState(state).activesData;

export const selectFunnelParams = (state: RootState) => ({
    managerId: selectFunnelDataState(state).currentManagerId,
    atonValuation: selectFunnelDataState(state).currentAtonValuation,
    selectedRating: selectFunnelDataState(state).currentSelectedRating,
    selectedClientIds: selectFunnelDataState(state).currentSelectedClientIds,
    financeGroupIncluded: selectFunnelDataState(state).currentFinanceGroupIncluded,
});

export const selectChartScopeSources = (state: RootState) =>
    selectFunnelDataState(state).chartScopeSources;

export const selectMergedFunnelClientPrimaryKeys = (state: RootState): number[] =>
    selectFunnelDataState(state).mergedFunnelClientPrimaryKeys ?? [];

export const selectValuationByScope = (state: RootState) =>
    selectFunnelDataState(state).valuationByScope;

export const selectRatingValuationScopeSnapshots = (state: RootState) =>
    selectFunnelDataState(state).ratingValuationScopeSnapshots;

export const selectRatingValuationScopesBootstrapReady = (state: RootState): boolean =>
    selectFunnelDataState(state).ratingValuationScopesBootstrapReady;

export default funnelDataSlice.reducer;